import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid
} from '@mui/material';
import { 
  Email as EmailIcon, 
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

// 设置axios默认配置 - 支持环境变量配置
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
axios.defaults.baseURL = API_BASE_URL;

function App() {
  // 状态管理
  const [apiStatus, setApiStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 新增状态 - Claude.ai验证系统
  const [eduEmail, setEduEmail] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(''); // waiting, received, expired
  const [verificationEmail, setVerificationEmail] = useState(null);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [monitoringActive, setMonitoringActive] = useState(false);

  // localStorage 键名
  const STORAGE_KEYS = {
    SESSION_ID: 'claude_verification_session_id',
    EDU_EMAIL: 'claude_verification_edu_email',
    VERIFICATION_STATUS: 'claude_verification_status',
    VERIFICATION_EMAIL: 'claude_verification_email'
  };

  // 从localStorage加载用户数据
  const loadUserData = () => {
    try {
      const storedSessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
      const storedEduEmail = localStorage.getItem(STORAGE_KEYS.EDU_EMAIL);
      const storedStatus = localStorage.getItem(STORAGE_KEYS.VERIFICATION_STATUS);
      const storedVerificationEmail = localStorage.getItem(STORAGE_KEYS.VERIFICATION_EMAIL);

      if (storedSessionId && storedEduEmail) {
        setSessionId(storedSessionId);
        setEduEmail(storedEduEmail);
        setVerificationStatus(storedStatus || '');
        
        if (storedVerificationEmail) {
          try {
            setVerificationEmail(JSON.parse(storedVerificationEmail));
          } catch (e) {
            console.error('解析存储的验证邮件失败:', e);
          }
        }

        // 如果状态是waiting，继续轮询
        if (storedStatus === 'waiting') {
          setMonitoringActive(true);
          startVerificationPolling(storedSessionId);
        }
      }
    } catch (error) {
      console.error('加载用户数据失败:', error);
    }
  };

  // 保存用户数据到localStorage
  const saveUserData = (sessionId, eduEmail, status, verificationEmail = null) => {
    try {
      localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
      localStorage.setItem(STORAGE_KEYS.EDU_EMAIL, eduEmail);
      localStorage.setItem(STORAGE_KEYS.VERIFICATION_STATUS, status);
      
      if (verificationEmail) {
        localStorage.setItem(STORAGE_KEYS.VERIFICATION_EMAIL, JSON.stringify(verificationEmail));
      } else {
        localStorage.removeItem(STORAGE_KEYS.VERIFICATION_EMAIL);
      }
    } catch (error) {
      console.error('保存用户数据失败:', error);
    }
  };

  // 清除用户数据
  const clearUserData = () => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error('清除用户数据失败:', error);
    }
  };

  // 检查API状态
  const checkApiStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/status');
      setApiStatus(response.data);
      setError('');
    } catch (err) {
      setError('无法连接到后端服务');
      setApiStatus(null);
    } finally {
      setLoading(false);
    }
  };

  // 注册edu邮箱
  const registerEduEmail = async () => {
    if (!eduEmail.trim()) {
      setError('请输入edu邮箱地址');
      return;
    }

    if (!eduEmail.endsWith('.edu')) {
      setError('请输入有效的.edu邮箱地址');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/register-edu-email', {
        edu_email: eduEmail
      });
      
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      setVerificationStatus('waiting');
      setMonitoringActive(true);
      setError('');
      
      // 保存到localStorage
      saveUserData(newSessionId, eduEmail, 'waiting');
      
      // 开始轮询检查验证状态
      startVerificationPolling(newSessionId);
      
    } catch (err) {
      setError(err.response?.data?.error || '注册失败');
    } finally {
      setLoading(false);
    }
  };

  // 轮询检查验证状态
  const startVerificationPolling = (sessionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/check-verification/${sessionId}`);
        const data = response.data;
        
        setVerificationStatus(data.status);
        
        if (data.status === 'received') {
          setVerificationEmail(data.verification_email);
          clearInterval(pollInterval);
          setMonitoringActive(false);
          
          // 更新localStorage
          saveUserData(sessionId, eduEmail, 'received', data.verification_email);
          
        } else if (data.status === 'expired') {
          clearInterval(pollInterval);
          setMonitoringActive(false);
          setError('验证会话已过期，请重新注册');
          
          // 更新localStorage
          saveUserData(sessionId, eduEmail, 'expired');
        } else {
          // 更新localStorage中的状态
          saveUserData(sessionId, eduEmail, data.status);
        }
      } catch (err) {
        console.error('检查验证状态失败:', err);
        // 如果会话不存在，清除本地数据
        if (err.response?.status === 404) {
          clearUserData();
          resetState();
        }
      }
    }, 5000); // 每5秒检查一次

    // 1小时后停止轮询
    setTimeout(() => {
      clearInterval(pollInterval);
      setMonitoringActive(false);
    }, 3600000);
  };

  // 获取验证邮件内容
  const getVerificationContent = async () => {
    try {
      const response = await axios.get(`/api/get-verification-content/${sessionId}`);
      setVerificationEmail(response.data);
      setShowVerificationDialog(true);
      
      // 更新localStorage
      saveUserData(sessionId, eduEmail, verificationStatus, response.data);
      
    } catch (err) {
      setError(err.response?.data?.error || '获取验证内容失败');
    }
  };

  // 重置状态
  const resetState = () => {
    setEduEmail('');
    setSessionId('');
    setVerificationStatus('');
    setVerificationEmail(null);
    setMonitoringActive(false);
    setError('');
    
    // 清除localStorage
    clearUserData();
  };

  // 格式化时间
  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleString('zh-CN');
  };

  // 获取状态图标和颜色
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'waiting':
        return { icon: <ScheduleIcon />, color: 'warning', text: '等待验证邮件' };
      case 'received':
        return { icon: <CheckCircleIcon />, color: 'success', text: '已接收验证邮件' };
      case 'expired':
        return { icon: <ErrorIcon />, color: 'error', text: '会话已过期' };
      default:
        return { icon: <EmailIcon />, color: 'default', text: '未知状态' };
    }
  };

  // 组件挂载时检查API状态和加载用户数据
  useEffect(() => {
    checkApiStatus();
    loadUserData(); // 加载用户本地数据
  }, []);

  return (
    <div className="App">
      <AppBar position="static" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <EmailIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Claude.ai EDU邮箱验证系统
          </Typography>
          <Button 
            color="inherit" 
            onClick={checkApiStatus}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            刷新状态
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* 系统状态显示 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            系统状态
          </Typography>
          {loading && <CircularProgress size={24} />}
          {apiStatus && (
            <Alert 
              severity={apiStatus.status === 'success' ? 'success' : 'error'}
              sx={{ mb: 2 }}
            >
              {apiStatus.message}
              {apiStatus.monitor_status && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  邮件监控状态: {apiStatus.monitor_status === 'running' ? '运行中' : '已停止'}
                </Typography>
              )}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* 单列布局 - 只显示EDU邮箱注册 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            注册EDU邮箱验证
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            输入您的.edu邮箱地址，系统将自动监控Claude.ai发送的验证邮件
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="EDU邮箱地址"
              value={eduEmail}
              onChange={(e) => setEduEmail(e.target.value)}
              placeholder="example@university.edu"
              disabled={monitoringActive}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={registerEduEmail}
              disabled={loading || monitoringActive}
              fullWidth
              sx={{ mb: 2 }}
            >
              {monitoringActive ? '监控中...' : '开始监控'}
            </Button>
            
            {monitoringActive && (
              <Button
                variant="outlined"
                onClick={resetState}
                fullWidth
              >
                停止监控
              </Button>
            )}
          </Box>

          {/* 验证状态显示 */}
          {verificationStatus && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getStatusDisplay(verificationStatus).icon}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {getStatusDisplay(verificationStatus).text}
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                  监控邮箱: {eduEmail}
                </Typography>
                
                {verificationStatus === 'waiting' && (
                  <Box sx={{ mt: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                      正在等待验证邮件...
                    </Typography>
                  </Box>
                )}
                
                {verificationStatus === 'received' && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={getVerificationContent}
                    sx={{ mt: 2 }}
                  >
                    查看验证邮件
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </Paper>

        {/* 验证邮件内容对话框 */}
        <Dialog
          open={showVerificationDialog}
          onClose={() => setShowVerificationDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Claude.ai 验证邮件
          </DialogTitle>
          <DialogContent>
            {verificationEmail && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>收件人:</strong> {verificationEmail.edu_email}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>主题:</strong> {verificationEmail.subject}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>接收时间:</strong> {formatTime(verificationEmail.received_time)}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                {verificationEmail.verification_link && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      <strong>验证链接:</strong>
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      href={verificationEmail.verification_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      fullWidth
                    >
                      点击验证登录
                    </Button>
                  </Box>
                )}
                
                <Typography variant="subtitle1" gutterBottom>
                  <strong>邮件内容:</strong>
                </Typography>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {verificationEmail.content}
                  </Typography>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowVerificationDialog(false)}>
              关闭
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}

export default App;